import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useToasts } from '@blaze-react/toaster';
import { LoadingComponent } from '@blaze-cms/react-page-builder';
import { useEcommerceContext } from '@blaze-cms/ecommerce-context';
import { useTranslation } from '@blaze-cms/nextjs-components';
import { CONTEXT_KEYS, BLAZE_DEFAULT_MIN_QUANTITY } from '../constants';
import { ADD_TO_CART_CLASSNAME } from './constants';
import CartQuantityInput from './CartQuantityInput';
import CartVariantSelect from './CartVariantSelect';
import { getTotalPrice } from './helpers';
import { TRANSLATION_NAMESPACE } from '../../constants';

const AddToCart = ({
  itemId,
  productVariants,
  selectedVariantData,
  addToText,
  preorderText,
  modifier
}) => {
  const { cartIdKey, variantKey } = CONTEXT_KEYS;
  const { t } = useTranslation(TRANSLATION_NAMESPACE);
  const {
    [cartIdKey]: cartId,
    isCartLoading,
    setContextState,
    handleCreateCart,
    handleAddItemsToCart
  } = useEcommerceContext();

  const {
    variantId,
    price,
    isAvailable,
    isPreorder,
    minQuantity,
    maxQuantity
  } = selectedVariantData;

  const { addToast = () => {} } = useToasts();
  const [quantity, setQuantity] = useState(minQuantity || BLAZE_DEFAULT_MIN_QUANTITY);

  const onError = error => {
    addToast(`Cart error: ${error.message}`, {
      appearance: 'error',
      autoDismiss: true
    });
  };

  const onCompleteCartAction = ({
    cartData: {
      result: { lineItems = [] }
    }
  }) => {
    const product =
      lineItems.find(
        ({ publishedProduct }) => publishedProduct && publishedProduct.id === itemId
      ) || {};
    addToast(`${product.name || t('PRODUCT')} ${t('ADDED_TO_BASKET')}`, {
      appearance: 'success',
      autoDismiss: true
    });
  };

  const handleVariantSelect = ({ target: { value } }) => {
    setContextState(variantKey, value);
    setQuantity(Number(BLAZE_DEFAULT_MIN_QUANTITY));
  };

  const handleQuantityChange = newQuantity => {
    setQuantity(newQuantity);
  };

  const handleAddToCart = () => {
    if (isCartLoading) return;
    const updatedCartItems = [{ productId: itemId, variantId, quantity: Number(quantity) }];
    const handler = cartId ? handleAddItemsToCart : handleCreateCart;
    handler({
      lineItems: updatedCartItems,
      onCompleted: onCompleteCartAction,
      onError
    });
  };

  const addToCartString = addToText || t('ADD_TO_CART_STRING');
  const preorderString = preorderText || t('ADD_TO_CART_PREORDER_STRING');
  const buttonText = isPreorder ? preorderString : addToCartString;
  const isDisabled = isAvailable === false;
  const totalPrice = getTotalPrice(price, quantity);
  const parsedModifier = modifier ? ` ${modifier}` : '';
  const className = `${ADD_TO_CART_CLASSNAME}${parsedModifier}`;

  return (
    <div className={className}>
      <CartVariantSelect
        variants={productVariants}
        selectedVariant={variantId}
        totalPrice={totalPrice}
        handleOnChange={handleVariantSelect}
        className={ADD_TO_CART_CLASSNAME}
      />
      <CartQuantityInput
        className={ADD_TO_CART_CLASSNAME}
        quantity={quantity}
        selectedVariant={variantId}
        handleOnChange={handleQuantityChange}
        minValue={minQuantity}
        maxValue={maxQuantity}
      />

      <button
        className={`${ADD_TO_CART_CLASSNAME}__button${isDisabled ? ' button--disabled' : ''}`}
        type="button"
        onClick={handleAddToCart}
        disabled={isDisabled}>
        {isCartLoading ? <LoadingComponent /> : buttonText}
      </button>
    </div>
  );
};

AddToCart.propTypes = {
  itemId: PropTypes.string.isRequired,
  productVariants: PropTypes.array.isRequired,
  selectedVariantData: PropTypes.object.isRequired,
  addToText: PropTypes.string,
  preorderText: PropTypes.string,
  modifier: PropTypes.string
};

AddToCart.defaultProps = {
  addToText: null,
  preorderText: null,
  modifier: null
};

export default AddToCart;
